// src/components/layout/SuperAdminLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import Navbar from '../Navbar'; // Usamos el Navbar reutilizable que ya creamos
import NotificationBell from '../NotificationBell';
import ChangelogWidget from '../ChangelogWidget';
import SelectorFraccionamientoRapido from '../SelectorFraccionamientoRapido';
import {
    FaTachometerAlt, FaUsers, FaSwimmingPool, FaShieldAlt, FaFileInvoiceDollar, FaBuilding, FaCogs, FaHome, FaBars, FaTimes,
    FaBullhorn, FaCalendarAlt, FaHandsHelping, FaRocketchat, FaFileAlt, FaUserEdit, FaChartBar, FaArrowUp, FaArrowDown,
    FaReceipt, FaWallet, FaQuestionCircle, FaCog, FaQrcode, FaBoxOpen, FaTruck, FaIdCard, FaTools, FaHardHat,
    FaHistory, FaDownload, FaCamera, FaCar, FaWarehouse, FaClipboardCheck, FaChartPie
} from 'react-icons/fa';
import { ResidentPendingProvider } from '../../context/ResidentPendingContext';
import { useAuth } from '../../context/AuthContext';
import { useModulos } from '../../hooks/useModulos';
import { useResumenCargosAutomaticos } from '../../hooks/useResumenCargosAutomaticos';
import ModalResumenCargosAutomaticos from '../ModalResumenCargosAutomaticos';
import ModalRegistroPago from '../ModalRegistroPago';
import { db, storage } from '../../firebase';
import { doc, getDoc, collection, onSnapshot, query, orderBy, getDocs, where, writeBatch, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './SuperAdminLayout.css'; // Crearemos este archivo para sus estilos
import NotificacionEmergente from '../NotificacionEmergente';
import DomusAIChatWidget from '../DomusAIChatWidget';
import AdminMobileRestriction from '../AdminMobileRestriction';
import Swal from 'sweetalert2';

const SuperAdminLayout = () => {
    const { userData, fraccionamientoId: activeFraccId } = useAuth();
    const fraccionamientoId = activeFraccId || userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

    // Identificar si es un teléfono (Pantalla pequeña < 600px)
    const isPhone = window.innerWidth < 600;

    // Escuchar alerta de mejoras reactiva global para administradores
    useEffect(() => {
        const configGlobalRef = doc(db, 'plataforma', 'config_global');
        const unsubscribe = onSnapshot(configGlobalRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const esAdmin = ['superadmin', 'platformadmin', 'admin'].includes(userData?.rol);
                const aplicaARol = data.rolesAlerta ? Boolean(data.rolesAlerta.administrador) : true;
                if (data.alertaMejorasActiva && data.alertaMejorasVersion && esAdmin && aplicaARol) {
                    const versionVista = localStorage.getItem('alerta_version_vista');
                    if (versionVista !== data.alertaMejorasVersion && !window.isAlertaMejorasActive) {
                        window.isAlertaMejorasActive = true;
                        Swal.fire({
                            title: data.alertaMejorasTitulo || '¡Actualización del Sistema!',
                            html: `<div style="text-align: left; font-size: 0.95rem; line-height: 1.5;">${data.alertaMejorasMensaje || 'Se han aplicado nuevas mejoras importantes al sistema. Es necesario recargar tu página para poder ver los cambios.'}</div>`,
                            icon: 'info',
                            confirmButtonText: 'Actualizar y Recargar',
                            confirmButtonColor: '#0284c7',
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            allowEnterKey: true
                        }).then((result) => {
                            if (result.isConfirmed) {
                                localStorage.setItem('alerta_version_vista', data.alertaMejorasVersion);
                                window.isAlertaMejorasActive = false;
                                window.location.reload(true);
                            }
                        });
                    }
                }
            }
        }, (err) => {
            console.warn('SuperAdminLayout: error escuchando config_global', err);
        });

        return () => unsubscribe();
    }, [userData]);

    const [modalPagoCasa, setModalPagoCasa] = useState(null);

    // Escuchar evento personalizado para abrir modal de registro de pago desde el Asistente IA
    useEffect(() => {
        const handleAbrirModalPago = async (event) => {
            const detail = event.detail || {};
            const casaNumero = detail.casaNumero;
            if (!casaNumero || !fraccionamientoId) return;
            try {
                const cleanNum = String(casaNumero).trim();
                const variantes = Array.from(new Set([
                    cleanNum,
                    cleanNum.toUpperCase(),
                    cleanNum.toLowerCase(),
                    cleanNum.replace(/^(?:casa|lote|dpto|unidad|vivienda|#)\s*/i, '').trim()
                ])).filter(Boolean);

                let docEncontrado = null;

                // 1. Buscar por campo 'numero'
                const snap1 = await getDocs(query(
                    collection(db, 'fraccionamientos', fraccionamientoId, 'casas'),
                    where('numero', 'in', variantes.slice(0, 10))
                ));
                if (!snap1.empty) docEncontrado = snap1.docs[0];

                // 2. Buscar por campo 'numeroCasa'
                if (!docEncontrado) {
                    const snap2 = await getDocs(query(
                        collection(db, 'fraccionamientos', fraccionamientoId, 'casas'),
                        where('numeroCasa', 'in', variantes.slice(0, 10))
                    ));
                    if (!snap2.empty) docEncontrado = snap2.docs[0];
                }

                // 3. Buscar por docId directo
                if (!docEncontrado) {
                    for (const v of variantes) {
                        const directDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'casas', v));
                        if (directDoc.exists()) {
                            docEncontrado = directDoc;
                            break;
                        }
                    }
                }

                if (docEncontrado) {
                    setModalPagoCasa({
                        id: docEncontrado.id,
                        ...docEncontrado.data(),
                        montoSugerido: detail.montoSugerido || 0,
                        comprobanteId: detail.comprobanteId || null,
                        comprobanteUrl: detail.comprobanteUrl || null,
                        nombreArchivo: detail.nombreArchivo || null,
                        notas: detail.notas || ''
                    });
                } else {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Casa no encontrada',
                        text: 'No se encontró la casa número ' + casaNumero + ' para registrar el pago.',
                        confirmButtonColor: '#4f46e5'
                    });
                }
            } catch (err) {
                console.error('Error al buscar casa para registrar pago:', err);
            }
        };

        window.addEventListener('domus_abrir_modal_pago', handleAbrirModalPago);
        return () => window.removeEventListener('domus_abrir_modal_pago', handleAbrirModalPago);
    }, [fraccionamientoId]);

    // Función oficial de confirmación de pago invocada desde el modal de cobro rápido
    const handleConfirmPagoLayout = async (datosPago, options = {}) => {
        if (!modalPagoCasa || !fraccionamientoId) return;

        const pagos = Array.isArray(datosPago) ? datosPago : (datosPago?.pagos || []);
        const totalMonto = datosPago?.totalMonto ?? options?.totalMonto ?? 0;
        const totalDescuento = datosPago?.totalDescuento ?? options?.totalDescuento ?? 0;
        const montoFinal = datosPago?.montoFinal ?? options?.montoFinal ?? 0;
        const folioBase = datosPago?.folioBase ?? options?.folioBase;
        const folioSerie = datosPago?.folioSerie ?? options?.folioSerie;

        if (!Array.isArray(pagos) || pagos.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No hay pagos válidos para registrar.',
                confirmButtonColor: '#ef4444'
            });
            return;
        }

        try {
            const batch = writeBatch(db);
            const ingresoRef = doc(collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos'));

            const folioSerieSafe = (folioSerie || 'R').replace(/[^A-Za-z]/g, '') || 'R';
            const folioConsecutivo = (folioBase !== null && folioBase !== undefined && !isNaN(folioBase))
                ? Number(folioBase)
                : null;
            const folioString = folioConsecutivo ? `${folioSerieSafe}${String(folioConsecutivo).padStart(6, '0')}` : null;

            const primerPago = pagos[0] || {};
            const conceptosResumen = pagos.map(p => p.concepto).filter(Boolean).join(', ');
            const conceptoFinal = pagos.length > 1 ? `Multi-Concepto: ${conceptosResumen}` : (primerPago.concepto || `Pago - Casa ${modalPagoCasa.numero || modalPagoCasa.id}`);

            const obsLista = pagos.map(p => (p.observaciones || '').trim()).filter(Boolean);
            const obsGeneralRoot = (datosPago?.observaciones || options?.observaciones || '').trim();

            const bloquesNotas = [];
            obsLista.forEach(nota => {
                if (!bloquesNotas.includes(nota)) {
                    bloquesNotas.push(nota);
                }
            });

            if (obsGeneralRoot && !bloquesNotas.some(b => b.includes(obsGeneralRoot))) {
                bloquesNotas.push(obsGeneralRoot);
            }

            const observacionesFinales = bloquesNotas.join('\n');

            const totalMontoCalculado = totalMonto || pagos.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
            const totalDescuentoCalculado = totalDescuento || pagos.reduce((sum, p) => sum + (parseFloat(p.descuento) || 0), 0);
            const totalNetoCalculado = montoFinal || pagos.reduce((sum, p) => sum + (parseFloat(p.montoNeto) || 0), 0);

            const cleanPagos = pagos.map(p => {
                const copy = { ...p };
                delete copy.comprobante;
                return copy;
            });

            let downloadURL = modalPagoCasa.comprobanteUrl || null;
            const primerPagoConComprobante = pagos.find(p => p.comprobante);
            if (primerPagoConComprobante && primerPagoConComprobante.comprobante instanceof File) {
                try {
                    const storageRef = ref(storage, `comprobantes/${fraccionamientoId}/${modalPagoCasa.id}_${Date.now()}_${primerPagoConComprobante.comprobante.name}`);
                    const snapshot = await uploadBytes(storageRef, primerPagoConComprobante.comprobante);
                    downloadURL = await getDownloadURL(snapshot.ref);
                } catch (uploadError) {
                    console.error('Error subiendo comprobante en SuperAdminLayout:', uploadError);
                }
            }

            const singleIngresoData = {
                fraccionamientoId,
                monto: parseFloat(totalMontoCalculado || 0),
                descuento: parseFloat(totalDescuentoCalculado || 0),
                descuentoPorcentaje: 0,
                montoNeto: parseFloat(totalNetoCalculado || 0),
                mesesPagadosAdelantado: pagos.reduce((sum, p) => sum + (p.mesesPagadosAdelantado || 0), 0),
                concepto: conceptoFinal,
                casaId: modalPagoCasa.id,
                casaNumero: modalPagoCasa.numero || '',
                fecha: serverTimestamp(),
                timestamp: serverTimestamp(),
                fechaPagoOriginal: primerPago.fechaPago || null,
                metodoPago: primerPago.metodoPago || '',
                referencia: primerPago.referencia || '',
                observaciones: observacionesFinales,
                comprobanteURL: downloadURL || null,
                multaAdministrativa: (pagos.find(p => p.multaAdministrativa)?.multaAdministrativa) || primerPago.multaAdministrativa || null,
                pagos: cleanPagos,
                esMultiConcepto: pagos.length > 1,
                ...(folioConsecutivo ? { folioConsecutivo, folioSerie: folioSerieSafe, folio: folioString } : {})
            };

            const sanitizeObject = (obj) => JSON.parse(JSON.stringify(obj, (k, v) => v === undefined ? null : v));
            const firestoreIngresoPayload = {
                ...sanitizeObject(singleIngresoData),
                fecha: serverTimestamp(),
                timestamp: serverTimestamp()
            };

            batch.set(ingresoRef, firestoreIngresoPayload);

            // Si vino comprobanteId, actualizar su estado en fraccionamientos/{id}/comprobantesPago a 'conciliado'
            if (modalPagoCasa.comprobanteId) {
                const compRef = doc(db, 'fraccionamientos', fraccionamientoId, 'comprobantesPago', modalPagoCasa.comprobanteId);
                batch.update(compRef, {
                    estado: 'conciliado',
                    ingresoId: ingresoRef.id,
                    folioPago: folioString || null,
                    montoConciliado: totalNetoCalculado,
                    fechaConciliacion: serverTimestamp(),
                    conciliadoPor: userData?.nombre || userData?.displayName || 'Administrador'
                });
            }

            await batch.commit();

            // Cerrar modal
            setModalPagoCasa(null);

            // Notificar éxito con SweetAlert2
            Swal.fire({
                icon: 'success',
                title: '¡Pago Registrado Exitosamente!',
                html: `<p>Se registró el pago de <strong>${totalNetoCalculado.toFixed(2)} MXN</strong> para la <strong>Casa ${modalPagoCasa.numero || modalPagoCasa.id}</strong>.</p>${folioString ? `<p style="font-size:0.95rem;color:#4f46e5;font-weight:700;">Folio: ${folioString}</p>` : ''}<p style="font-size:0.85rem;color:#64748b;">El sistema contable ha procesado la amortización del adeudo de la vivienda.</p>`,
                confirmButtonColor: '#10b981'
            });
        } catch (err) {
            console.error('Error al registrar pago en SuperAdminLayout:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error al Registrar Pago',
                text: 'Hubo un error al procesar el pago: ' + (err.message || 'Error de conexión') + '. Intente nuevamente.',
                confirmButtonColor: '#ef4444'
            });
        }
    };

    const { modulos, loading } = useModulos(fraccionamientoId);
    const { resumenNoVisto, marcarComoVisto } = useResumenCargosAutomaticos();
    const location = useLocation();
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const checkIsMobile = () => window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const [isMobile, setIsMobile] = useState(checkIsMobile);
    const [fraccionamientoConfig, setFraccionamientoConfig] = useState({
        logoUrl: null,
        condominioNombre: null
    });
    const navigate = useNavigate();

    // Detectar cambios en el tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            const mobile = checkIsMobile();
            setIsMobile(mobile);
            // En móvil, ocultar sidebar por defecto
            if (mobile) {
                setSidebarVisible(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Llamar una vez al montar

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Ocultar sidebar automáticamente cuando cambia la ruta (solo en móvil)
    useEffect(() => {
        if (isMobile) {
            setSidebarVisible(false);
        }
    }, [location.pathname, isMobile]);

    const getInitials = (name) => {
        if (!name) return '';
        const parts = name.trim().split(/\s+/).filter(p => p.length > 0);
        if (parts.length === 0) return '';
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // Cargar configuración del fraccionamiento (logo y nombre)
    useEffect(() => {
        let isMounted = true;
        const loadFraccionamientoConfig = async () => {
            if (!fraccionamientoId) return;

            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (isMounted && fraccDoc.exists()) {
                    const data = fraccDoc.data() || {};
                    const configRecibos = data.configuracionRecibos || {};

                    const logoUrl = configRecibos.logoUrl || data.logoUrl || null;
                    const condominioNombre = configRecibos.condominioNombre || data.condominioNombre || data.nombre || null;

                    setFraccionamientoConfig({
                        logoUrl,
                        condominioNombre
                    });
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error al cargar configuración del fraccionamiento:', error);
                }
            }
        };

        loadFraccionamientoConfig();

        return () => { isMounted = false; };
    }, [fraccionamientoId]);

    // Manejar botón de retroceso nativo de la app (solo en móvil)
    useEffect(() => {
        if (!isMobile) return;

        const isNative = Capacitor.isNativePlatform();
        let backButtonListener = null;

        if (isNative) {
            backButtonListener = App.addListener('backButton', ({ canGoBack }) => {
                if (sidebarVisible) {
                    // Si el sidebar está abierto, cerrarlo
                    setSidebarVisible(false);
                } else if (canGoBack) {
                    // Si el navegador puede ir hacia atrás, hacerlo
                    window.history.back();
                } else {
                    // Si no puede ir hacia atrás, salir de la app
                    App.exitApp();
                }
            });
        }

        return () => {
            if (backButtonListener) {
                backButtonListener.remove();
            }
        };
    }, [isMobile, sidebarVisible]);

    // Restablecer scroll del contenedor de contenido principal al cambiar de página (pathname)
    useEffect(() => {
        const contentContainer = document.querySelector('.superadmin-content');
        if (contentContainer) {
            contentContainer.scrollTop = 0;
        }
    }, [location.pathname]);

    // Determinar si algún módulo de vigilancia está habilitado
    // Primero verificar que el módulo principal de vigilancia esté activo
    const tieneModulosVigilancia = modulos.vigilancia === true && (
        modulos.validar_qr === true
        || modulos.registrar_paquete === true
        || modulos.generar_qr_invitado === true
        || modulos.visitas_frecuentes === true
    );

    // Si está cargando, mostramos los enlaces por defecto
    console.log('🔵 [SuperAdminLayout] Evaluando enlaces:', {
        loading,
        modulos,
        amenidades: modulos.amenidades,
        tipo_amenidades: typeof modulos.amenidades,
        es_true: modulos.amenidades === true,
        es_false: modulos.amenidades === false
    });

    // Función para crear submenús optimizados (solo en móvil)
    const createSubmenus = (baseLink) => {
        if (!isMobile) return baseLink; // No submenús en desktop

        let submenu = [];

        if (baseLink.to === '/comunidad') {
            submenu = [
                { to: '/comunidad/avisos', label: 'Avisos', icon: <FaBullhorn /> },
                ...(modulos.chat_comunitario === true ? [{ to: '/comunidad/chat', label: 'Chat Comunitario', icon: <FaRocketchat /> }] : []),
                { to: '/comunidad/propuestas', label: 'Propuestas e Incidencias', icon: <FaHandsHelping /> },
                { to: '/comunidad/contacto-proveedores', label: 'Directorio de Proveedores', icon: <FaUserEdit /> },
            ];
        } else if (baseLink.to === '/configuraciones') {
            submenu = [
                { to: '/configuraciones/perfil', label: 'Mi Perfil', icon: <FaUserEdit /> },
                { to: '/configuraciones/soporte', label: 'Soporte y Ayuda', icon: <FaCog /> },
            ];
        }

        if (submenu.length > 0) {
            return { ...baseLink, submenu };
        }

        return baseLink;
    };

    // Enlaces para la versión Móvil (operatividad ágil y seguridad, sin saturación contable)
    const mobileLinks = [
        ...(loading || tieneModulosVigilancia ? [{ to: '/vigilancia', label: 'Vigilancia y Seguridad', icon: <FaShieldAlt /> }] : []),
        ...(loading || modulos.comunidad === true ? [{ to: '/comunidad', label: 'Comunidad', icon: <FaUsers /> }] : []),
        ...(loading || modulos.amenidades === true ? [{ to: '/amenidades', label: 'Amenidades', icon: <FaSwimmingPool /> }] : []),
        ...(loading || modulos.administracion === true ? [{ to: '/administracion/departamentos/residentes', label: 'Residentes', icon: <FaBuilding /> }] : []),
        ...(loading || modulos.configuraciones === true ? [{ to: '/configuraciones', label: 'Ajustes', icon: <FaCogs /> }] : []),
    ];

    // Enlaces completos para la versión Escritorio
    const desktopLinks = [
        { to: '/dashboard', label: 'Dashboard', icon: <FaTachometerAlt /> },
        ...(loading || modulos.comunidad === true ? [{ to: '/comunidad', label: 'Comunidad', icon: <FaUsers /> }] : []),
        ...(loading || modulos.amenidades === true ? [{ to: '/amenidades', label: 'Amenidades', icon: <FaSwimmingPool /> }] : []),
        ...(loading || tieneModulosVigilancia ? [{ to: '/vigilancia', label: 'Vigilancia', icon: <FaShieldAlt /> }] : []),
        ...(loading || modulos.finanzas === true ? [{ to: '/finanzas', label: 'Finanzas', icon: <FaFileInvoiceDollar /> }] : []),
        ...(loading || modulos.administracion === true ? [{ to: '/administracion', label: 'Administración', icon: <FaBuilding /> }] : []),
        ...(loading || modulos.configuraciones === true ? [{ to: '/configuraciones', label: 'Configuraciones', icon: <FaCogs /> }] : []),
    ];

    const baseLinks = isMobile ? mobileLinks : desktopLinks;

    // Crear submenús para los links
    const superAdminLinks = baseLinks.map(link => createSubmenus(link));

    const toggleSidebar = () => {
        setSidebarVisible(!sidebarVisible);
    };

    // Verificar si estamos en el chat comunitario
    const isChatComunitario = location.pathname === '/comunidad/chat';

    // Restricción móvil retirada para permitir acceso de administración desde dispositivos móviles
    // (Selección granular de secciones e información para administradores en móvil)


    return (
        <ResidentPendingProvider>
            <div className={`superadmin-layout ${isMobile ? (sidebarVisible ? 'sidebar-visible' : 'sidebar-hidden') : 'desktop-mode'}`}>
                {/* Elementos fijos en móvil (solo cuando sidebar cerrada, no en chat comunitario) */}
                {isMobile && !sidebarVisible && !isChatComunitario && (
                    <>
                        {/* Iniciales en esquina superior izquierda */}
                        <div className="user-initials-fixed-wrapper">
                            <div className="user-initials-circle">
                                {getInitials(userData?.nombre || userData?.nombreCompleto || 'Usuario')}
                            </div>
                        </div>

                        {/* Megáfono y Campana en esquina superior derecha */}
                        <div className="fixed-header-actions-wrapper">
                            <ChangelogWidget isStatic={true} />
                            <NotificationBell />
                        </div>
                    </>
                )}

                {/* Contenedor fijo superior para evitar traslapes en redimensionados */}
                <div className="superadmin-top-container">
                    {/* Header con branding y notificaciones */}
                    <header className="superadmin-header">
                        <div className="superadmin-header-content">
                            <div className="superadmin-branding" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                {fraccionamientoConfig.logoUrl ? (
                                    <img
                                        src={fraccionamientoConfig.logoUrl}
                                        alt={fraccionamientoConfig.condominioNombre || 'Logo'}
                                        className="superadmin-brand-logo"
                                        style={{ height: '42px', maxWidth: '120px', objectFit: 'contain', borderRadius: '6px', background: '#ffffff', padding: '3px 6px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
                                    />
                                ) : (
                                    <FaHome className="superadmin-brand-icon" />
                                )}
                                <div className="superadmin-brand-text">
                                    <h1>
                                        Administración del Fraccionamiento
                                        {(fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre) && (
                                            <span className="superadmin-brand-condo-name" style={{ opacity: 0.95, fontWeight: '700', marginLeft: '6px' }}>
                                                — {fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre}
                                            </span>
                                        )}
                                    </h1>
                                    <p>Panel de Control</p>
                                </div>
                            </div>
                            <div className="superadmin-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Link
                                    to="/finanzas/metricas-bolsas"
                                    className="superadmin-header-bolsas-btn"
                                    title="Métricas de Bolsas Financieras (Ingresos vs Egresos)"
                                >
                                    <FaChartPie className="bolsas-header-icon" />
                                    <span className="bolsas-header-label">Bolsas Financieras</span>
                                </Link>
                                {!isMobile && <NotificationBell />}
                            </div>
                        </div>
                    </header>

                    {/* Navbar horizontal para desktop */}
                    {!isMobile && (
                        <div className="superadmin-navbar-wrapper">
                            <Navbar links={superAdminLinks} isMobile={false} showSubmenus={false} />
                        </div>
                    )}
                </div>

                {/* Sidebar para móvil */}
                {isMobile && (
                    <aside className={`superadmin-sidebar ${sidebarVisible ? 'visible' : 'hidden'}`}>
                        <div className="superadmin-sidebar-header">
                            <div className="superadmin-sidebar-profile-card">
                                <div className="superadmin-sidebar-avatar-container">
                                    <div className="superadmin-sidebar-avatar">
                                        {getInitials(userData?.nombre || userData?.nombreCompleto || 'Usuario')}
                                    </div>
                                    <span className="superadmin-sidebar-status-dot" title="En línea" />
                                </div>
                                <div className="superadmin-sidebar-user-details">
                                    <div className="superadmin-sidebar-name-row">
                                        <span className="superadmin-sidebar-user-name">{userData?.nombre || userData?.nombreCompleto || 'Administrador'}</span>
                                        <span className={`superadmin-sidebar-role-pill ${(userData?.rol || 'superadmin').toLowerCase()}`}>
                                            {['platformadmin', 'manager'].includes((userData?.rol || '').toLowerCase()) ? 'PLATFORM ADMIN' : 'ADMINISTRADOR'}
                                        </span>
                                    </div>
                                    {(fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre) && (
                                        <span className="superadmin-sidebar-condo-badge">
                                            <FaBuilding style={{ fontSize: '0.75rem', flexShrink: 0 }} />
                                            <span className="condo-name-text">{fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                className="superadmin-sidebar-close"
                                onClick={toggleSidebar}
                                aria-label="Cerrar menú"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="superadmin-sidebar-content">
                            <Navbar links={superAdminLinks} isMobile={true} showSubmenus={true} />
                        </div>
                        <div className="superadmin-sidebar-footer">
                            {fraccionamientoConfig.logoUrl ? (
                                <img src={fraccionamientoConfig.logoUrl} alt="Logo" className="superadmin-sidebar-footer-logo" />
                            ) : (
                                <img src="/domus-logo.jpg" alt="Logo" className="superadmin-sidebar-footer-logo" />
                            )}
                            <p className="superadmin-sidebar-footer-name">{fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre || 'Domus Habita'}</p>
                            <p className="superadmin-sidebar-footer-copyright">© 2026 Domus Habita. Todos los derechos reservados</p>
                        </div>
                    </aside>
                )}

                <main className="superadmin-content">
                    <Outlet /> {/* Aquí se renderizará tu SuperAdminDashboard.js */}
                </main>

                {/* Botón flotante siempre visible en móvil (solo cuando sidebar está oculto, no en chat comunitario) */}
                {isMobile && !sidebarVisible && !isChatComunitario && (
                    <button
                        className="sidebar-float-btn"
                        onClick={toggleSidebar}
                        aria-label="Mostrar menú"
                        title="Mostrar menú"
                    >
                        <FaBars />
                    </button>
                )}

                {/* Overlay para cerrar el sidebar al hacer clic fuera en móviles */}
                {isMobile && sidebarVisible && (
                    <div
                        className="sidebar-overlay"
                        onClick={toggleSidebar}
                        onTouchStart={toggleSidebar}
                        aria-hidden="true"
                    />
                )}

                {/* Modal de resumen de cargos automáticos */}
                {resumenNoVisto && (
                    <ModalResumenCargosAutomaticos
                        resumen={resumenNoVisto}
                        onClose={() => marcarComoVisto(resumenNoVisto.id)}
                        onMarkAsRead={marcarComoVisto}
                    />
                )}

                {/* Modal de Registro de Pago rápido invocado por Asistente IA */}
                {modalPagoCasa && (
                    <ModalRegistroPago
                        casa={modalPagoCasa}
                        onClose={() => setModalPagoCasa(null)}
                        onConfirm={handleConfirmPagoLayout}
                    />
                )}

                {/* Notificaciones Emergentes Críticas */}
                <NotificacionEmergente />

                {/* Asistente Virtual Inteligente (IA) */}
                <DomusAIChatWidget fraccionamientoId={fraccionamientoId} />
            </div>
        </ResidentPendingProvider>
    );
};

export default SuperAdminLayout;